import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import { dwisendPaymentAPI } from './services/api';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import InvestmentMarketplace from './pages/InvestmentMarketplace';
import Wallet from './pages/Wallet';
import Exchange from './pages/Exchange';
import CryptoWithdraw from './pages/CryptoWithdraw';
import Agent from './pages/Agent';
import Merchant from './pages/Merchant';
import Notifications from './pages/Notifications';
import ISP from './pages/ISP'
import InternetPackages from './pages/InternetPackages';
import BuyPackage from './pages/BuyPackage';
import Referral from './pages/Referral';
import Analytics from './pages/Analytics';
import KYC from './pages/KYC';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import EnterpriseAdmin from './pages/EnterpriseAdmin';
import AdminSettings from './pages/AdminSettings';
import SuperAdmin from './pages/SuperAdmin';
import TreasuryAdmin    from './pages/TreasuryAdmin';
import CountriesAdmin   from './pages/CountriesAdmin';
import CmsAdmin         from './pages/CmsAdmin';
import OperationsCenter from './pages/OperationsCenter';
import AdminKYC         from './pages/AdminKYC';
import DwiConnect from './pages/DwiConnect';
import PublicChargePage from './pages/PublicChargePage';
import MerchantPayPage from './pages/MerchantPayPage';
import InvoicePayPage   from './pages/InvoicePayPage';
import MerchantApiDocs  from './pages/MerchantApiDocs';
import LinkPackage      from './pages/LinkPackage';
import EventRegistration from './pages/EventRegistration';
import PartnerPortal    from './pages/Partnership';
import Market           from './pages/Market';
import SocialHub        from './pages/SocialHub';
import DwiSendApprovals from './pages/DwiSendApprovals';
import DwiSendLogisticsApprovals from './pages/DwiSendLogistics';
import ClientModuleGate from './components/layout/ClientModuleGate';
import DwiSendHub, { DrcLegacyRedirect } from './pages/DwiSendHub';   // DRC: one DwiSend page with Boutique · Mes commandes · Demandes
import { DRC_HOME } from './components/layout/regionalNav';
import PartnerInterest  from './pages/PartnerInterest';
import MarketLanding    from './pages/MarketLanding';
import { safeNext }     from './utils/safeNext';
import PartnerInviteAccept from './pages/PartnerInviteAccept';
import CaptivePortal from './pages/CaptivePortal';
import { Spinner } from './components/ui';
import FeatureGate from './components/FeatureGate';
import { TrendingUp } from 'lucide-react';


class MerchantErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  componentDidCatch(e, i) { console.error('[MerchantConsole] crash:', e, i); }
  render() {
    if (this.state.err) return (
      <div style={{ padding: 32, background: '#0a1628', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 16, padding: 24, maxWidth: 560, width: '100%' }}>
          <p style={{ color: '#f87171', fontWeight: 700, marginBottom: 8 }}>Merchant Console failed to load</p>
          <p style={{ color: '#94a3b8', fontSize: 13 }}>{this.state.err.message}</p>
          <pre style={{ color: '#fca5a5', fontSize: 11, marginTop: 12, overflow: 'auto', maxHeight: 200 }}>{this.state.err.stack}</pre>
          <button onClick={() => this.setState({ err: null })} style={{ marginTop: 16, color: '#06c8e8', fontSize: 12, cursor: 'pointer', background: 'none', border: 'none' }}>↩ Retry</button>
        </div>
      </div>
    );
    return this.props.children;
  }
}
class PublicErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  componentDidCatch(e, i) { console.error('[PublicRoute] crash:', e, i); }
  render() {
    if (this.state.err) return (
      <div style={{ padding: 32, background: '#0A1628', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <p style={{ color: '#f87171', fontWeight: 700, marginBottom: 8 }}>Page failed to load</p>
          <p style={{ color: '#94a3b8', fontSize: 13 }}>{this.state.err.message}</p>
          <button onClick={() => this.setState({ err: null })} style={{ marginTop: 16, color: '#06c8e8', fontSize: 12, cursor: 'pointer', background: 'none', border: 'none' }}>↩ Retry</button>
        </div>
      </div>
    );
    return this.props.children;
  }
}
function ProtectedRoute() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
}

// Accounts based outside Uganda (DRC) don't have the Uganda wallet/investments
// dashboard — their home is DwiSend requests, or, for a DwiSend payment
// approver, their approvals page. Admins and Ugandan accounts (no country or
// UG) see the Dashboard exactly as before.
function Home() {
  const { user, isAdmin } = useAuth();
  const regional = !isAdmin && !!user?.countryCode && user.countryCode !== 'UG';
  const [dest, setDest] = useState(null);
  useEffect(() => {
    if (!regional) return;
    dwisendPaymentAPI.status()
      .then(r => setDest(r.youAreApprover ? '/dwisend-approvals' : DRC_HOME))
      .catch(() => setDest(DRC_HOME));   // not an approver (403) or offline
  }, [regional]);
  if (!regional) return <Dashboard />;
  return dest ? <Navigate to={dest} replace /> : null;
}

// Old /dwi-market and /dwi-market/p/<slug> links → /dwisend, keeping ?src= etc.
function OldMarketLink() {
  const { slug } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/dwisend${slug ? `/p/${encodeURIComponent(slug)}` : ''}${search}${hash}`} replace />;
}

function PublicRoute() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }
  // Already signed in and arriving from a ?next= link (e.g. the DWI Market page) → go straight there.
  const next = new URLSearchParams(window.location.search).get('next');
  return isAuthenticated ? <Navigate to={safeNext(next)} replace /> : <Outlet />;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#111927',
            color: '#E8EFF8',
            border: '1px solid #1C2B40',
            borderRadius: '12px',
            fontSize: '14px',
          },
          duration: 4000,
        }}
      />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Public routes */}
        <Route element={<PublicRoute />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<PublicErrorBoundary><Register /></PublicErrorBoundary>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />   {/* one-time link from a reset email or an admin-issued setup link */}
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/dashboard"    element={<Home />} />
            <Route path="/wallet"       element={<Wallet />} />
            <Route path="/exchange"        element={<ClientModuleGate moduleKey="buy_usdt"><Exchange /></ClientModuleGate>} />
            <Route path="/crypto-withdraw" element={<ClientModuleGate moduleKey="crypto_withdraw"><CryptoWithdraw /></ClientModuleGate>} />
            <Route path="/investments"  element={
              <FeatureGate feature="investment_projects" fallback={
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:60, textAlign:'center', minHeight:'60vh' }}>
                  <TrendingUp size={48} color="#4E677E" style={{ marginBottom:16 }} />
                  <h2 style={{ fontSize:20, fontWeight:700, color:'#F0F6FF', margin:'0 0 8px' }}>Investments Not Available</h2>
                  <p style={{ fontSize:14, color:'#8BA4C0', margin:0 }}>This feature is not currently available. Check back soon.</p>
                </div>
              }>
                <InvestmentMarketplace />
              </FeatureGate>
            } />
            <Route path="/agent"        element={<Agent />} />
            <Route path="/merchant"     element={<MerchantErrorBoundary><Merchant /></MerchantErrorBoundary>} />
            <Route path="/notifications"element={<Notifications />} />
            <Route path="/isp"          element={<ClientModuleGate moduleKey="isp"><ISP /></ClientModuleGate>} />
            <Route path="/internet" element={<InternetPackages />} />
            <Route path="/market" element={<Market />} />
            <Route path="/dwisend-drc" element={<DwiSendHub />} />   {/* DRC clients' DwiSend: Boutique · Mes commandes · Demandes — the server decides who may use each */}
            <Route path="/dwisend-requests" element={<DrcLegacyRedirect tab="requests" />} />   {/* old links (incl. notifications) → the tab */}
            <Route path="/dwisend-shop" element={<DrcLegacyRedirect tab="shop" />} />
            <Route path="/dwisend-approvals" element={<DwiSendApprovals />} />   {/* DwiSend payment approvers — the server refuses anyone else */}
            <Route path="/dwisend-logistics-approvals" element={<DwiSendLogisticsApprovals />} />   {/* DwiSend approvers: cross-border routes + partner payments (logistics, records only) */}
            <Route path="/socialhub" element={<SocialHub />} />   {/* DWI SocialHub — page shows "not available" unless the socialhub flag is on for this user */}
            <Route path="/buy-package/:packageId" element={<BuyPackage />} />
            <Route path="/referral"     element={<ClientModuleGate moduleKey="referral"><Referral /></ClientModuleGate>} />
            <Route path="/analytics"    element={<ClientModuleGate moduleKey="analytics"><Analytics /></ClientModuleGate>} />
            <Route path="/kyc"          element={<KYC />} />
            <Route path="/settings"     element={<Settings />} />
            <Route path="/partner-portal" element={<PartnerPortal />} />
            <Route path="/admin"          element={<Admin />} />
            <Route path="/admin-settings" element={<AdminSettings />} />
          </Route>
        </Route>

        {/* Enterprise Core — standalone operations center, manages its own auth */}
        <Route path="/enterprise" element={<EnterpriseAdmin />} />

        {/* Super Admin — standalone, manages its own token read */}
        <Route path="/superadmin"      element={<SuperAdmin />} />
        <Route path="/treasury-admin"  element={<TreasuryAdmin />} />
        <Route path="/countries-admin" element={<CountriesAdmin />} />
        <Route path="/cms-admin"       element={<CmsAdmin />} />
        <Route path="/operations"      element={<OperationsCenter />} />
        <Route path="/admin-kyc"       element={<AdminKYC />} />

        {/* DwiConnect — standalone portal, manages own auth */}
        <Route path="/dwiconnect" element={<DwiConnect />} />
        <Route path="/pay/c/:chargeId"      element={<PublicChargePage />} />
        <Route path="/pay/m/:merchantCode" element={<MerchantPayPage />} />
        <Route path="/pay/i/:invoiceId"    element={<InvoicePayPage />} />
        <Route path="/merchant/docs"   element={<MerchantApiDocs />} />

        {/* Reference-code self-service linking — reached via a direct SMS/WhatsApp
            link (?code=DWI-XXXXX), must render regardless of login state */}
        <Route path="/link-package" element={<LinkPackage />} />

        {/* Public launch-event RSVP — no login required */}
        <Route path="/launch" element={<EventRegistration />} />

        {/* Public partner interest / lead capture — no login required */}
        <Route path="/partner-interest" element={<PartnerInterest />} />
        <Route path="/dwisend" element={<MarketLanding />} />   {/* public marketing page — own switch, Admin → DwiSend → Settings */}
        <Route path="/dwisend/p/:slug" element={<MarketLanding />} />   {/* public product page — shareable link, same switch */}
        {/* Old DWI Market links (ads, bookmarks, shared products) → the same page under /dwisend.
            nginx already 301s these; this covers the SPA if it is ever reached directly. */}
        <Route path="/dwi-market" element={<OldMarketLink />} />
        <Route path="/dwi-market/p/:slug" element={<OldMarketLink />} />

        {/* Public partner invite acceptance — no login required (this IS
            how a not-yet-registered invitee gets an account). Not flag-gated,
            matching the backend invite-accept endpoints it calls. */}
        <Route path="/partner-invite" element={<PartnerInviteAccept />} />

        {/* DWIFI Captive Portal — public, self-contained, no login required.
            Reached from the MikroTik walled garden once a router is live;
            testable against the live APIs today without one. */}
        <Route path="/dwifi" element={<CaptivePortal />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
